// ============================================
// FILE: /frontend/src/pages/admin/SubscriptionsPage.jsx
// PURPOSE: Admin page for managing user subscriptions
// ============================================

import { useState, useEffect } from 'react';
import { format, differenceInDays } from 'date-fns';
import { subscriptionsAPI } from '../../api/subscriptions.api';
import { useToast } from '../../hooks/useToast';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowUpDown, Calendar, CreditCard, Users, AlertTriangle, CheckCircle, XCircle, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Pagination } from '../../components/ui/Pagination';
import { Input } from '../../components/ui/input';

// ========================================
// LOADING SKELETON
// ========================================
const LoadingSkeleton = () => (
  <CardContent>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Phone</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead>Days to Expiry</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Renewals</TableHead>
          <TableHead>Last Renewed</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton className="h-5 w-32" /></TableCell>
            <TableCell><Skeleton className="h-5 w-28" /></TableCell>
            <TableCell><Skeleton className="h-5 w-48" /></TableCell>
            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
            <TableCell><Skeleton className="h-5 w-16" /></TableCell>
            <TableCell><Skeleton className="h-6 w-24 rounded-full" /></TableCell>
            <TableCell><Skeleton className="h-5 w-12" /></TableCell>
            <TableCell><Skeleton className="h-5 w-28" /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </CardContent>
);

const StatsCardSkeleton = () => (
  <Card>
    <CardHeader>
      <Skeleton className="h-4 w-24 mb-2" />
      <Skeleton className="h-8 w-16" />
    </CardHeader>
  </Card>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function SubscriptionsPage() {
  console.log('🎨 [SubscriptionsPage] Component mounted');

  const { toast } = useToast();

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortBy, setSortBy] = useState('subscriptionEndDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterTier, setFilterTier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, sortBy, sortOrder, filterTier, filterStatus, filterRole, searchQuery]);

  const fetchStats = async () => {
    console.log('🔵 [SubscriptionsPage] Fetching stats...');
    setLoadingStats(true);

    try {
      const data = await subscriptionsAPI.getSubscriptionStats();
      console.log('✅ [SubscriptionsPage] Stats received:', data);
      setStats(data.stats);
    } catch (err) {
      console.error('❌ [SubscriptionsPage] Fetch stats error:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchSubscriptions = async () => {
    console.log('🔵 [SubscriptionsPage] Fetching subscriptions...', {
      page: currentPage,
      sortBy,
      sortOrder,
      filterTier,
      filterStatus,
      filterRole,
      searchQuery
    });
    setLoading(true);

    try {
      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder
      };

      if (filterTier !== 'all') params.tier = filterTier;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterRole !== 'all') params.role = filterRole;
      if (searchQuery) params.search = searchQuery;

      const data = await subscriptionsAPI.getAllSubscriptions(params);
      console.log('✅ [SubscriptionsPage] Subscriptions received:', data);
      
      setUsers(data.users || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalUsers(data.pagination?.total || 0);
      setError(null);
    } catch (err) {
      console.error('❌ [SubscriptionsPage] Fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load subscriptions');
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load subscriptions'
      });
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleTierFilterChange = (value) => {
    setFilterTier(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (value) => {
    setFilterRole(value);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getTierBadgeVariant = (tier) => {
    switch (tier) {
      case 'PLATINUM':
        return 'default';
      case 'GOLD':
        return 'secondary';
      case 'SILVER':
        return 'outline';
      case 'BRONZE':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'expiring_soon':
        return 'secondary';
      case 'expired':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'expiring_soon':
        return <AlertTriangle className="h-4 w-4" />;
      case 'expired':
        return <XCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'expiring_soon':
        return 'Expiring Soon';
      case 'expired':
        return 'Expired';
      default:
        return 'No Subscription';
    }
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="space-y-6">
      {/* STATS CARDS */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {loadingStats ? (
          <>
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
          </>
        ) : stats ? (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalUsers}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.activeSubscriptions}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
                <AlertTriangle className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</div>
                <p className="text-xs text-muted-foreground">Next 7 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Expired</CardTitle>
                <XCircle className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.expiredSubscriptions}</div>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {/* MAIN SUBSCRIPTIONS TABLE */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Management</CardTitle>
          <CardDescription>View and manage all user subscriptions</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* SEARCH BAR */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>

          {/* FILTERS ROW */}
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Filter by Tier</label>
              <Select value={filterTier} onValueChange={handleTierFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All Tiers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="BRONZE">Bronze</SelectItem>
                  <SelectItem value="SILVER">Silver</SelectItem>
                  <SelectItem value="GOLD">Gold</SelectItem>
                  <SelectItem value="PLATINUM">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Filter by Status</label>
              <Select value={filterStatus} onValueChange={handleStatusFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Filter by Role</label>
              <Select value={filterRole} onValueChange={handleRoleFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="USER">User</SelectItem>
                  <SelectItem value="DEALER">Dealer</SelectItem>
                  <SelectItem value="REAL_ESTATE_AGENT">Real Estate Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Total Results</label>
              <div className="h-10 px-3 border rounded-md flex items-center bg-muted">
                <span className="font-semibold">{totalUsers}</span>
              </div>
            </div>
          </div>
        </CardContent>

        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <CardContent>
            <p className="text-destructive">Error: {error}</p>
          </CardContent>
        ) : (
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSortChange('fullName')}
                        className="hover:bg-transparent"
                      >
                        Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSortChange('role')}
                        className="hover:bg-transparent"
                      >
                        Role
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSortChange('subscriptionTier')}
                        className="hover:bg-transparent"
                      >
                        Tier
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSortChange('subscriptionEndDate')}
                        className="hover:bg-transparent"
                      >
                        Days to Expiry
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Renewals</TableHead>
                    <TableHead>Last Renewed</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {users?.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.fullName || 'N/A'}</TableCell>
                      <TableCell>{user.phone || 'N/A'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {user.role?.toLowerCase().replace('_', ' ') || 'user'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.subscriptionTier ? (
                          <Badge variant={getTierBadgeVariant(user.subscriptionTier)} className="capitalize">
                            {user.subscriptionTier.toLowerCase()}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.daysToExpiry !== null ? (
                          <span className={
                            user.daysToExpiry < 0 
                              ? 'text-red-600 font-semibold'
                              : user.daysToExpiry <= 7
                              ? 'text-orange-600 font-semibold'
                              : 'text-green-600'
                          }>
                            {user.daysToExpiry < 0 
                              ? `Expired ${Math.abs(user.daysToExpiry)} days ago`
                              : `${user.daysToExpiry} days`
                            }
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={getStatusBadgeVariant(user.subscriptionStatus)} 
                          className="flex items-center gap-1 w-fit"
                        >
                          {getStatusIcon(user.subscriptionStatus)}
                          {formatStatus(user.subscriptionStatus)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold">{user.renewalCount}</span>
                      </TableCell>
                      <TableCell>
                        {user.lastRenewalDate ? (
                          <div>
                            <p className="text-sm">{format(new Date(user.lastRenewalDate), 'PPP')}</p>
                            <p className="text-xs text-muted-foreground">
                              KES {parseFloat(user.lastRenewalAmount).toLocaleString()}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Never</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {users?.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No subscriptions found.</p>
            )}

            {/* PAGINATION */}
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={totalUsers}
                itemsPerPage={20}
                showInfo={true}
              />
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}